/**
 * i18n 便捷 Hook
 *
 * 简化 UmiJS useIntl().formatMessage() 的调用，提供更简洁的 API。
 *
 */

import { useCallback, useMemo } from 'react';

/**
 * 国际化函数类型
 */
type IntlFormatMessage = (
  descriptor: { id: string; defaultMessage?: string },
  values?: Record<string, string | number>,
) => string;

/**
 * useIntlText 配置
 */
export interface UseIntlTextOptions {
  /** formatMessage 函数（通常来自 useIntl()） */
  formatMessage: IntlFormatMessage;
  /** key 前缀，自动拼接到所有 id 前面 */
  prefix?: string;
}

/**
 * useIntlText 返回值
 */
export interface UseIntlTextReturn {
  /** 获取国际化文本 */
  t: (
    id: string,
    defaultMessage?: string,
    values?: Record<string, string | number>,
  ) => string;
  /** 获取国际化文本（带前缀的简写） */
  tp: (
    id: string,
    defaultMessage?: string,
    values?: Record<string, string | number>,
  ) => string;
}

/**
 * i18n 便捷 Hook
 *
 * @example
 * ```tsx
 * import { useIntl } from '@umijs/max';
 *
 * function MyComponent() {
 *   const intl = useIntl();
 *   const { t, tp } = useIntlText({
 *     formatMessage: intl.formatMessage,
 *     prefix: 'pages.user',
 *   });
 *
 *   return (
 *     <div>
 *       <h1>{t('app.title', '应用标题')}</h1>
 *       <p>{tp('name', '用户名')}</p>  {// => pages.user.name}
 *       <p>{tp('greeting', '你好, {name}', { name: 'Alice' })}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useIntlText(options: UseIntlTextOptions): UseIntlTextReturn {
  const { formatMessage, prefix } = options;

  const t = useCallback(
    (
      id: string,
      defaultMessage?: string,
      values?: Record<string, string | number>,
    ): string => {
      return formatMessage(
        { id, defaultMessage: defaultMessage ?? id },
        values,
      );
    },
    [formatMessage],
  );

  const tp = useCallback(
    (
      id: string,
      defaultMessage?: string,
      values?: Record<string, string | number>,
    ): string => {
      const fullId = prefix ? `${prefix}.${id}` : id;
      return formatMessage(
        { id: fullId, defaultMessage: defaultMessage ?? id },
        values,
      );
    },
    [formatMessage, prefix],
  );

  return useMemo(() => ({ t, tp }), [t, tp]);
}
